import React from 'react'
import {useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import {OfferInteraction} from 'components/Offer/Shared/OfferInteraction'
import {SplitBookingOfferInteraction} from 'components/Offer/Shared/SplitBookingOfferInteraction'
import {useShowAccommodationPage} from 'hooks/useShowAccommodationPage'
import {
  getCurrencyCode,
  getIsRtlLanguage,
  getUserMeasurementSystem
} from 'modules/meta/selectors'
import {getOfferAccessFlowSettings} from 'modules/sapiSearch/selectors'
import {getListOfValues} from 'modules/search/selectors'
import {setActiveHotelId} from 'modules/search/slice'
import {imageProvider} from 'utils/imageProvider'

import {Divider} from '@vio/atlas/Divider'
import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {Image} from '@vio/atlas/Image'
import {StarRating} from '@vio/atlas/StarRating'
import {Tag} from '@vio/atlas/Tag'
import {Text} from '@vio/atlas/Text'
import {useIsEmbeddedWithBrand} from '@vio/core/src/_web/brand/hooks/useIsEmbeddedWithBrand'
import {convertMilesToKm} from '@vio/core/src/utils/distanceFrom'
import {PriceComposition} from '@vio/shared/src/search/PriceComposition'
import {ProviderLogo} from '@vio/shared/src/search/ProviderLogo'
import {ReviewRating} from '@vio/shared/src/search/ReviewRating'

import {badgeMessages, distanceMessages, perkMessages} from './constants'
import {Badge, Card, CardLink, HotelName, PhotoWrapper} from './styles'

import type {SimilarHotelRecommendation} from './types'

interface Props {
  recommendation: SimilarHotelRecommendation
  onClick: React.MouseEventHandler<HTMLAnchorElement>
}

const getDisplayDistance = (
  distanceKm: number | undefined,
  isImperial: boolean
) => {
  if (distanceKm === undefined) return undefined

  const distance = isImperial ? distanceKm / convertMilesToKm(1) : distanceKm
  // Keep plural selection consistent with the displayed distance.
  const roundedDistance = Number(distance.toFixed(1))

  return Math.max(0.1, roundedDistance)
}

export const SimilarHotelCard = ({recommendation, onClick}: Props) => {
  const {
    hotel,
    displayPrice,
    distanceKm,
    badge,
    perks,
    offer,
    providerOffers,
    splitBookingOfferUrl,
    anchorPriceTotal
  } = recommendation
  const dispatch = useDispatch()
  const {formatMessage} = useIntl()
  const currency = useSelector(getCurrencyCode)
  const isRtlLanguage = useSelector(getIsRtlLanguage)
  const listOfValues = useSelector(getListOfValues)
  const isImperial = useSelector(getUserMeasurementSystem) === 'imperial'
  const isEmbedded = useIsEmbeddedWithBrand()
  const {getAccommodationPageUrl} = useShowAccommodationPage()
  const hotelId = hotel.objectID
  const displayDistance = getDisplayDistance(distanceKm, isImperial)
  const isAuthLocked = useSelector(
    state => getOfferAccessFlowSettings(state, hotelId, offer?.id).isAuthLocked
  )
  const isLocked = Boolean(offer && !splitBookingOfferUrl && isAuthLocked)

  const footer = (
    <Box
      display="flex"
      flexWrap="wrap"
      alignItems="center"
      columnGap="s300"
      paddingTop="s400"
      paddingBottom="s400"
      paddingLeft="s400"
      paddingRight="s250"
    >
      <Box display="flex" flexDirection="column" gap="s200" data-id="Logo">
        {isLocked ? (
          <Icon
            name="EngineeredPrivateDeal"
            size="sm"
            colorPath="content.brand.primary"
          />
        ) : (
          (providerOffers ?? (offer ? [offer] : []))
            .filter(
              (providerOffer, index, offers) =>
                offers.findIndex(
                  other =>
                    other.intermediaryProvider ===
                    providerOffer.intermediaryProvider
                ) === index
            )
            .map(providerOffer => (
              <ProviderLogo
                key={providerOffer.intermediaryProvider}
                providerCode={providerOffer.intermediaryProvider}
                brandInfo={providerOffer.metadata?.brandInfo}
                height={16}
              />
            ))
        )}
      </Box>
      <Box
        display="flex"
        flex="1 0 auto"
        justifyContent="flex-end"
        alignItems="center"
        gap="s200"
      >
        <PriceComposition
          dataId="Price"
          anchorPrice={0}
          size="md"
          showRowLayout
          currencyCode={currency}
          price={displayPrice}
          isPrivateDeal={isLocked}
        />
        <Icon
          name="ChevronRight"
          size="sm"
          hasMirror
          colorPath={
            isLocked ? 'content.brand.primary' : 'content.neutral.secondary'
          }
        />
      </Box>
    </Box>
  )

  return (
    <Card
      onMouseEnter={() => dispatch(setActiveHotelId(hotelId))}
      onMouseLeave={() => dispatch(setActiveHotelId(null))}
    >
      <CardLink
        href={getAccommodationPageUrl(hotelId)}
        target={isEmbedded ? '_self' : '_blank'}
        rel="noopener noreferrer"
        onClick={onClick}
      >
        <PhotoWrapper>
          <Image
            imageProvider={imageProvider}
            url={hotel.imageURIs?.[0] ?? ''}
            isResponsive
            size="large"
          />
          {badge && (
            <Badge>
              <Tag size="sm" variant="accent">
                {formatMessage(badgeMessages[badge])}
              </Tag>
            </Badge>
          )}
        </PhotoWrapper>
        <Box display="flex" flex="1" flexDirection="column" minWidth="0">
          <Box
            display="flex"
            flexDirection="column"
            gap="s250"
            minWidth="0"
            paddingTop="s300"
            paddingLeft="s400"
            paddingRight="s400"
          >
            <div>
              <Box display="flex" alignItems="center" gap="s200" minWidth="0">
                {hotel.starRating > 0 && (
                  <StarRating rating={hotel.starRating} size="xs" />
                )}
                <Text
                  tag="span"
                  variant="labelXS"
                  colorPath="content.neutral.secondary"
                >
                  {listOfValues?.[
                    `PropertyType:${hotel.propertyTypeId}`
                  ]?.value?.toString()}
                </Text>
              </Box>
              <div title={hotel.hotelName}>
                <HotelName
                  variant="labelM"
                  dir="ltr"
                  isRtlLanguage={isRtlLanguage}
                >
                  {hotel.hotelName}
                </HotelName>
              </div>
            </div>
            <ReviewRating
              rating={hotel.guestRating?.overall ?? 0}
              totalRatingCount={hotel.reviewCount ?? 0}
              variant="simple-default"
              singleLineLabels
            />
            {distanceKm !== undefined && (
              <Box display="flex" alignItems="center" gap="s200" minWidth="0">
                <Icon name="Pin" size="sm" />
                <Text variant="bodyXS">
                  {formatMessage(
                    isImperial
                      ? distanceMessages.miles
                      : distanceMessages.kilometers,
                    {distance: displayDistance}
                  )}
                </Text>
              </Box>
            )}
          </Box>
          <Box
            display="flex"
            flexWrap="wrap"
            alignContent="flex-start"
            gap="s200"
            padding="s400"
          >
            {perks.map(perk => (
              <Tag
                key={perk.kind}
                size="xs"
                variant={
                  perk.kind === 'freeCancellation' ||
                  perk.kind === 'breakfast' ||
                  perk.kind === 'cheaper'
                    ? 'success'
                    : 'neutralOutline'
                }
              >
                {formatMessage(perkMessages[perk.kind], {percent: perk.value})}
              </Tag>
            ))}
          </Box>
        </Box>
      </CardLink>
      <Divider />
      {splitBookingOfferUrl ? (
        <SplitBookingOfferInteraction
          component="similarHotels"
          hotelId={hotelId}
        >
          {footer}
        </SplitBookingOfferInteraction>
      ) : offer ? (
        <OfferInteraction
          component="similarHotels"
          hotelId={hotelId}
          offer={offer}
          anchorPriceTotal={anchorPriceTotal}
          offerPosition={0}
        >
          {footer}
        </OfferInteraction>
      ) : (
        footer
      )}
    </Card>
  )
}
